import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    ArrowLeft, Wallet, Apple, ShieldOff, AlertTriangle, CheckCircle2,
} from "lucide-react";

/**
 * /settings/wallet — VAULT Smart Pass settings.
 * Adds Apple / Google Wallet buttons, category toggles, and privacy warning.
 * All money-side privacy defaults are enforced on the backend — this UI
 * simply reflects them.
 */
const CATEGORIES = [
    { key: "payments",      label: "Payments" },
    { key: "subscriptions", label: "Subscriptions" },
    { key: "renewals",      label: "Renewals" },
    { key: "documents",     label: "Documents" },
    { key: "together",      label: "Together items" },
];

function isIOS() {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isAndroid() {
    if (typeof navigator === "undefined") return false;
    return /Android/.test(navigator.userAgent);
}

export default function WalletSettings() {
    const [state, setState] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [busy, setBusy] = useState(false);
    const [apple, setApple] = useState(null);
    const [google, setGoogle] = useState(null);

    const load = async () => {
        try {
            const [s, r] = await Promise.all([
                api.get("/api/wallet/settings"),
                api.get("/api/wallet/readiness"),
            ]);
            setState(s.data);
            setReadiness(r.data);
        } catch { toast.error("Couldn't load wallet settings"); }
    };
    useEffect(() => { load(); }, []);

    const patch = async (patchBody) => {
        setBusy(true);
        try {
            const r = await api.patch("/api/wallet/settings", patchBody);
            setState((prev) => ({ ...prev, settings: r.data.settings }));
        } catch { toast.error("Save failed"); }
        finally { setBusy(false); }
    };

    const toggleAmounts = (val) => {
        if (val) {
            const warn = state?.warning ||
                "Wallet info may be visible on your device. Only enable if you're comfortable displaying amounts.";
            if (!window.confirm(warn)) return;
        }
        patch({ show_amounts: val });
    };

    const toggleCategory = (key, val) => {
        patch({ categories: { [key]: val } });
    };

    const toggleTogether = (val) => patch({ together_pass: val });

    const addApple = async () => {
        setBusy(true);
        try {
            const r = await api.post("/api/wallet/apple/create");
            setApple(r.data);
            if (r.data.dry_run) {
                toast.info("Apple Wallet dry-run — waiting on Apple certificate");
            } else {
                toast.success("Apple Wallet pass created");
                await api.post("/api/wallet/events", { event: "apple_wallet_added" });
                if (r.data.add_url) window.location.href = r.data.add_url;
            }
        } catch (e) {
            if (e?.response?.status === 402) toast.error("Your plan doesn't include Smart Pass yet");
            else toast.error("Apple Wallet failed");
        } finally { setBusy(false); }
    };

    const addGoogle = async () => {
        setBusy(true);
        try {
            const r = await api.post("/api/wallet/google/create");
            setGoogle(r.data);
            if (r.data.dry_run) {
                toast.info("Google Wallet dry-run — waiting on Issuer account");
            } else {
                toast.success("Google Wallet pass created");
                await api.post("/api/wallet/events", { event: "google_wallet_added" });
                if (r.data.add_url) window.location.href = r.data.add_url;
            }
        } catch (e) {
            if (e?.response?.status === 402) toast.error("Your plan doesn't include Smart Pass yet");
            else toast.error("Google Wallet failed");
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        if (!window.confirm("Remove your Smart Pass? Passes on your devices will stop updating.")) return;
        setBusy(true);
        try {
            await api.post("/api/wallet/revoke");
            toast.success("Smart Pass removed");
            setApple(null); setGoogle(null);
        } finally { setBusy(false); }
    };

    if (!state) {
        return <div className="min-h-screen bg-[#f6f3ec] flex items-center justify-center text-stone-500">Loading…</div>;
    }

    const settings = state.settings;
    const entitled = state.entitled;
    const showApple  = !isAndroid();  // iOS + desktop
    const showGoogle = !isIOS();      // Android + desktop

    return (
        <div data-testid="wallet-settings" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-2xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/settings" className="text-stone-500 hover:text-stone-800"><ArrowLeft size={18} /></Link>
                    <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">Settings · Wallet</div>
                        <h1 className="font-['Fraunces',serif] text-2xl">VAULT Smart Pass</h1>
                        <div className="text-sm text-stone-600">Your financial life. One glance away.</div>
                    </div>
                </div>

                {!entitled && (
                    <Card className="mb-4 border-amber-200 bg-amber-50/60">
                        <CardContent className="pt-4 pb-4 flex items-start gap-3 text-sm text-amber-900">
                            <AlertTriangle size={16} className="mt-0.5" />
                            <div>Smart Pass is included on PRO, FAMILY and BUSINESS plans. Your current plan: <b>{state.plan}</b>.</div>
                        </CardContent>
                    </Card>
                )}

                <Card className="mb-4">
                    <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Add to your Wallet</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        {showApple && (
                            <Button data-testid="wallet-add-apple" onClick={addApple}
                                disabled={busy || !entitled}
                                className="w-full bg-black text-white hover:bg-black/90 h-11">
                                <Apple size={16} className="mr-2" /> Add to Apple Wallet
                            </Button>
                        )}
                        {showGoogle && (
                            <Button data-testid="wallet-add-google" onClick={addGoogle}
                                disabled={busy || !entitled} variant="outline"
                                className="w-full border-stone-300 h-11">
                                <Wallet size={16} className="mr-2" /> Add to Google Wallet
                            </Button>
                        )}
                        {(apple?.dry_run || google?.dry_run) && (
                            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                                A wallet-side certificate is being provisioned. Your VAULT-side setup is complete — the pass will attach automatically once Apple/Google credentials land.
                            </div>
                        )}
                        <Button variant="ghost" size="sm" onClick={revoke}
                            data-testid="wallet-revoke"
                            className="w-full text-rose-700 hover:text-rose-900 hover:bg-rose-50">
                            <ShieldOff size={14} className="mr-1" /> Remove Smart Pass
                        </Button>
                    </CardContent>
                </Card>

                <Card className="mb-4">
                    <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Display values</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <label className="flex items-center justify-between text-sm">
                            <div>
                                <div className="font-medium">Show amounts in Wallet</div>
                                <div className="text-xs text-stone-500 mt-0.5">{state.warning}</div>
                            </div>
                            <Switch data-testid="wallet-show-amounts"
                                checked={settings.show_amounts}
                                onCheckedChange={toggleAmounts} disabled={busy} />
                        </label>
                        <label className="flex items-center justify-between text-sm pt-2 border-t border-stone-100">
                            <div>
                                <div className="font-medium">Include Together items</div>
                                <div className="text-xs text-stone-500 mt-0.5">Adds a separate Together pass with only shared items.</div>
                            </div>
                            <Switch data-testid="wallet-together"
                                checked={settings.together_pass}
                                onCheckedChange={toggleTogether} disabled={busy} />
                        </label>
                    </CardContent>
                </Card>

                <Card className="mb-4">
                    <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">What appears on the pass</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {CATEGORIES.map(c => (
                            <label key={c.key} className="flex items-center justify-between text-sm py-1">
                                <span>{c.label}</span>
                                <Switch data-testid={`wallet-cat-${c.key}`}
                                    checked={!!settings.categories?.[c.key]}
                                    onCheckedChange={(v) => toggleCategory(c.key, v)}
                                    disabled={busy} />
                            </label>
                        ))}
                    </CardContent>
                </Card>

                {readiness && (
                    <Card>
                        <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Readiness</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {Object.entries(readiness.checks).map(([k, v]) => {
                                    const ok = v === "PASS" || v === "READY";
                                    return (
                                        <div key={k} className={`flex items-center gap-2 px-2 py-1 rounded border ${
                                            ok ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                               : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                                            {ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                                            <span className="font-medium">{k.replace(/_/g, " ")}</span>
                                            <span className="ml-auto">{v}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="text-[11px] text-stone-500 mt-3">
                                Final status: <b>{readiness.final_status}</b>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
